import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { QualificationRow } from "./qualifications";

/**
 * Therapist service qualifications (0037) — the data layer.
 *
 * WHY THIS SCREEN EXISTS. `staff_service_qualifications` gates both engines: an
 * unqualified therapist is blocked with UNQUALIFIED, so an empty table makes
 * every ASAP search find nobody and every Scheduled date read Fully Booked.
 * The dev seed fills it with rows marked `source = 'placeholder'`, which 0037's
 * own constraint enforces cannot name a grantor. Until this screen existed
 * there was no way to turn a placeholder into a confirmed fact, so Luigi's
 * roster had nowhere to land.
 *
 * ═══ WRITES GO THROUGH THE TWO RPCs, NEVER THE TABLE ═══
 *
 * 0037 grants no insert, update or delete on the table to anyone — the only
 * write paths are `grant_service_qualification` and
 * `revoke_service_qualification`, both `security definer`, both writing an
 * `audit_log` row, and both refusing a caller with no `auth.uid()`. That last
 * part is deliberate: a confirmed qualification must name a real human, so a
 * migration or a job cannot manufacture one.
 *
 * ═══ SUPERADMIN ONLY, AND THAT IS NOT AN OVERSIGHT ═══
 *
 * Execute is granted to `superadmin` and `service_role` and to nobody else —
 * not hr, not admin, not hub_manager. Who may certify a therapist for a service
 * is undefined in the vault, so SECURITY §9a's rule applies: build the
 * capability, gate it at superadmin, audit every use, and wait for Luigi to
 * assign the authority. Widening this to `admin` because it is inconvenient
 * would be inventing that decision.
 *
 * REVOKE IS A SOFT DELETE. `revoked_at` is stamped and the row stays; a delete
 * trigger refuses outright. A qualification that existed when a booking was
 * taken is part of why that booking was allowed.
 */

/** Every live (unrevoked) qualification, for cross-referencing in the grid. */
export function useQualifications() {
  return useQuery({
    queryKey: ["qualifications"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("staff_service_qualifications")
        .select("id, staff_id, service_id, source, note, granted_by, created_at")
        .is("revoked_at", null);
      if (error) throw error;
      return (data ?? []) as QualificationRow[];
    },
    staleTime: 60_000,
  });
}

/** Active therapists, hub-scoped by RLS for a hub manager and global for admin. */
export function useTherapists() {
  return useQuery({
    queryKey: ["qualification-therapists"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("staff")
        .select("id, full_name, hub_id, active")
        .eq("role", "therapist")
        .eq("active", true)
        .order("full_name");
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 5 * 60_000,
  });
}

export function useServices() {
  return useQuery({
    queryKey: ["qualification-services"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("services")
        .select("id, name, category")
        .order("category")
        .order("name");
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 5 * 60_000,
  });
}

function useInvalidate() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ["qualifications"] });
}

export interface QualificationMutation {
  staffId: string;
  serviceId: string;
  note?: string;
}

/**
 * Confirm a qualification. The RPC stamps `source = 'confirmed'` and
 * `granted_by = auth.uid()` itself — neither is sent from here, because a
 * client-supplied grantor is exactly the invented fact the table exists to
 * prevent.
 */
export function useGrantQualification() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async ({ staffId, serviceId, note }: QualificationMutation) => {
      const { data, error } = await supabase.rpc("grant_service_qualification", {
        p_staff_id: staffId,
        p_service_id: serviceId,
        p_note: note,
      });
      if (error) throw error;
      return data as { outcome: string; id: string };
    },
    onSuccess: invalidate,
  });
}

export function useRevokeQualification() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async ({ staffId, serviceId, note }: QualificationMutation) => {
      const { data, error } = await supabase.rpc("revoke_service_qualification", {
        p_staff_id: staffId,
        p_service_id: serviceId,
        p_note: note,
      });
      if (error) throw error;
      return data as { outcome: string; id: string | null };
    },
    onSuccess: invalidate,
  });
}
